/* SpawnAnything — pure mixing helpers (no WebAudio): distance attenuation, stereo pan from the screen,
   per-frame aggregation of thousands of battle events into a few voices, and a voice limiter.
   Kept free of DOM/three so they run in Node tests. */

/** gain for a sound `d` metres from the listener: 1 inside `ref`, then (ref/d)^roll, 0 beyond `max` */
export function distGain(d, ref = 30, roll = 1.1, max = 900) {
  if (d <= ref) return 1;
  if (d >= max) return 0;
  return Math.pow(ref / d, roll) * (1 - (d - ref) / (max - ref)) ** 0.5;
}

/**
 * Stereo pan (-1 left … +1 right) of a world point from a column-major view-projection matrix `vp`
 * (three.js `elements` order). Points behind the camera keep their side but are pulled towards the centre.
 */
export function screenPan(vp, x, y, z) {
  const cx = vp[0] * x + vp[4] * y + vp[8] * z + vp[12];
  const cw = vp[3] * x + vp[7] * y + vp[11] * z + vp[15];
  if (Math.abs(cw) < 1e-6) return 0;
  let p = cx / Math.abs(cw);
  if (cw < 0) p *= 0.5;
  return p < -1 ? -0.9 : p > 1 ? 0.9 : p * 0.9;
}

/** loudness boost for `n` simultaneous events of one kind (a crowd of swords is louder than one, but not n times) */
export const crowdBoost = n => n <= 1 ? 1 : Math.min(2.2, 1 + 0.32 * Math.log2(n));

const K = 4; // strongest events remembered per category and frame
/**
 * Aggregates battle events per category and frame, then emits at most `max` voices per category,
 * no more often than every `cd` seconds. Events on cooldown keep accumulating (they make the next voice louder).
 * cats: { name: { max = 1, cd = 0.05, gain = 1, act = 1 } } (act = how much this category counts as "fighting")
 */
export class Aggregator {
  constructor(cats) {
    this.names = Object.keys(cats);
    this.cfg = this.names.map(k => ({ max: 1, cd: 0.05, gain: 1, act: 1, ...cats[k] }));
    this.index = Object.fromEntries(this.names.map((k, i) => [k, i]));
    const C = this.names.length;
    this.n = new Float64Array(C); this.sumW = new Float64Array(C); this.sumWP = new Float64Array(C);
    this.topW = new Float64Array(C * K); this.topP = new Float64Array(C * K); this.topR = new Float64Array(C * K);
    this.last = new Float64Array(C).fill(-1e9);
    this.out = Array.from({ length: 64 }, () => ({ cat: '', ci: 0, gain: 0, pan: 0, rate: 1 }));
    this.activity = 0; this.near = 0; // since the last flush: raw event count weighted by act, and distance-weighted
  }
  /** one event: category, attenuation weight 0..1, pan -1..1, optional playback-rate hint (e.g. giants lower) */
  add(cat, w, pan, rate = 1) {
    const c = this.index[cat]; if (c === undefined) return;
    const cfg = this.cfg[c];
    this.n[c]++; this.activity += cfg.act; this.near += w * cfg.act;
    if (w <= 0) return;
    this.sumW[c] += w; this.sumWP[c] += w * pan;
    // keep the K strongest (insertion into a tiny sorted list, no allocation)
    const o = c * K;
    if (w <= this.topW[o + K - 1]) return;
    let j = K - 1;
    while (j > 0 && this.topW[o + j - 1] < w) { this.topW[o + j] = this.topW[o + j - 1]; this.topP[o + j] = this.topP[o + j - 1]; this.topR[o + j] = this.topR[o + j - 1]; j--; }
    this.topW[o + j] = w; this.topP[o + j] = pan; this.topR[o + j] = rate;
  }
  /** emit voices for this frame; returns the number written into this.out (pooled objects, valid until the next flush) */
  flush(now) {
    let v = 0;
    for (let c = 0; c < this.names.length; c++) {
      const n = this.n[c]; if (!n) continue;
      const cfg = this.cfg[c];
      if (now - this.last[c] < cfg.cd) continue; // cooling down: keep accumulating
      const o = c * K, sumW = this.sumW[c];
      if (sumW > 0) {
        const voices = Math.min(cfg.max, n, K), boost = crowdBoost(n) / Math.sqrt(voices), mean = this.sumWP[c] / sumW;
        for (let i = 0; i < voices && v < this.out.length; i++) {
          const w = this.topW[o + i]; if (w < 0.01) break;
          const V = this.out[v++];
          V.cat = this.names[c]; V.ci = c; V.rate = this.topR[o + i] || 1;
          V.gain = Math.min(1, cfg.gain * w * boost); // crowds get louder, but never past full scale
          V.pan = voices === 1 && n > 1 ? this.topP[o] * 0.6 + mean * 0.4 : this.topP[o + i];
        }
        this.last[c] = now;
      }
      this.n[c] = 0; this.sumW[c] = 0; this.sumWP[c] = 0;
      for (let i = 0; i < K; i++) this.topW[o + i] = 0;
    }
    return v;
  }
  /** returns and resets the activity counters since the last call: { all, near } */
  takeActivity(out) { out.all = this.activity; out.near = this.near; this.activity = 0; this.near = 0; return out; }
  reset() { this.n.fill(0); this.sumW.fill(0); this.sumWP.fill(0); this.topW.fill(0); this.last.fill(-1e9); this.activity = this.near = 0; }
}

/**
 * Caps simultaneously playing voices. Low-priority voices only get the first `lowShare` of the slots,
 * so explosions, UI and stingers always find room.
 */
export class VoiceLimiter {
  constructor(max = 28, lowShare = 0.75) { this.max = max; this.lowMax = Math.floor(max * lowShare); this.ends = new Float64Array(max); this.n = 0; }
  active(now) {
    let k = 0;
    for (let i = 0; i < this.n; i++) if (this.ends[i] > now) this.ends[k++] = this.ends[i];
    this.n = k; return k;
  }
  /** true if a voice of length `dur` (s) may start at `now`; prio: 0 low (battle), 1 high (always below max) */
  acquire(now, dur, prio = 0) {
    const a = this.active(now);
    if (a >= (prio ? this.max : this.lowMax)) return false;
    this.ends[this.n++] = now + dur;
    return true;
  }
}

/** exponential smoothing towards x with time constant tau (s) */
export const smooth = (cur, x, dt, tau) => cur + (x - cur) * (1 - Math.exp(-dt / tau));
/** event rate (events/s) -> 0..1 intensity; `half` = rate that gives ~0.5 */
export const rateToIntensity = (rate, half) => 1 - Math.exp(-rate * Math.LN2 / half);
/** slow motion pitches sounds down: playback-rate factor for a sim time scale */
export const slowMoRate = ts => ts >= 1 ? 1 : Math.max(0.45, 0.5 + 0.5 * ts);
